<template>
    <div class="vx-row">
        <div class="vx-col w-full" id="leaves-manage-table">
            <vx-card class="mb-3" subtitle="Year">
                <div class="vx-row">
                    <div class="vx-col w-1/4">
                        <v-select name="year" :closeOnSelect="true" :clearable="false" v-model="current_year"
                            :options="year_list" :dir="$vs.rtl ? 'rtl' : 'ltr'" label="name"
                            @input="onChangeYear">
                        </v-select>
                    </div>
                </div>
            </vx-card>
            <!-- Leave Stats Card -->
            <vx-card :title="`Annual Leave Statistics [${current_year}]`" class="mb-6">
                <template slot="actions">
                    <feather-icon icon="CalendarIcon" class="text-primary" />
                </template>

                <div v-if="my_leaves_stats && my_leaves_stats.stats && my_leaves_stats.stats.entitlement"
                    class="leave-stats">
                    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <!-- Total Allowance -->
                        <div class="stat-card bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 vx-card card-border no-shadow">
                            <div class="flex items-center justify-between">
                                <div class="flex" style="gap: 1.5rem;">
                                    <div>
                                        <h3 class="text-sm font-medium opacity-90">Total Allowance</h3>
                                        <p class="text-2xl font-bold mt-1">{{ my_leaves_stats.stats.entitlement.total_allowance }}</p>
                                        <span class="text-xs opacity-75">days/year</span>
                                    </div>
                                    <div style="border-left: 1px solid rgba(255,255,255,0.3); padding-left: 1.5rem;">
                                        <h3 class="text-sm font-medium opacity-90">Annual Allowance</h3>
                                        <p class="text-2xl font-bold mt-1">{{ my_leaves_stats.stats.entitlement.total_days }}</p>
                                        <span class="text-xs opacity-75">days</span>
                                    </div>
                                    <div style="border-left: 1px solid rgba(255,255,255,0.3); padding-left: 1.5rem;">
                                        <h3 class="text-sm font-medium opacity-90">Carried Forward</h3>
                                        <p class="text-2xl font-bold mt-1">{{ my_leaves_stats.stats.entitlement.carried_forward_days }}</p>
                                        <span class="text-xs opacity-75">days</span>
                                    </div>
                                </div>
                                <feather-icon icon="CalendarIcon" size="24" class="opacity-75" />
                            </div>
                        </div>

                        <!-- Used -->
                        <div class="stat-card bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-4 vx-card card-border no-shadow">
                            <div class="flex items-center justify-between">
                                <div>
                                    <h3 class="text-sm font-medium opacity-90">Used</h3>
                                    <p class="text-2xl font-bold mt-1">{{ my_leaves_stats.stats.entitlement.used }}</p>
                                    <span class="text-xs opacity-75">days</span>
                                </div>
                                <feather-icon icon="CheckCircleIcon" size="24" class="opacity-75" />
                            </div>
                        </div>

                        <!-- Pending -->
                        <div class="stat-card bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-4 vx-card card-border no-shadow">
                            <div class="flex items-center justify-between">
                                <div>
                                    <h3 class="text-sm font-medium opacity-90">Pending Approval</h3>
                                    <p class="text-2xl font-bold mt-1">{{ my_leaves_stats.stats.entitlement.pending }}
                                    </p>
                                    <span class="text-xs opacity-75">days</span>
                                </div>
                                <feather-icon icon="ClockIcon" size="24" class="opacity-75" />
                            </div>
                        </div>

                        <!-- Remaining -->
                        <div class="stat-card bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 vx-card card-border no-shadow">
                            <div class="flex items-center justify-between">
                                <div>
                                    <h3 class="text-sm font-medium opacity-90">Remaining</h3>
                                    <p class="text-2xl font-bold mt-1">{{ my_leaves_stats.stats.entitlement.remaining }}
                                    </p>
                                    <span class="text-xs opacity-75">days</span>
                                </div>
                                <feather-icon icon="PlusCircleIcon" size="24" class="opacity-75" />
                            </div>
                        </div>
                    </div>

                    <!-- Progress Bar -->
                    <div class="mt-6">
                        <div class="flex justify-between text-sm text-gray-600 mb-2">
                            <span>Leave Usage Progress</span>
                            <span>{{ getUsagePercentage() }}% Used</span>
                        </div>
                        <div class="w-full bg-white rounded-full h-2 border border-solid">
                            <div class="progress-bar h-2 rounded-full transition-all duration-500"
                                v-bind:style="{ width: getUsagePercentage() + '%' }"></div>
                        </div>
                    </div>
                </div>

                <div v-else class="flex items-center justify-center py-8">
                    <div class="text-center">
                        <feather-icon icon="AlertCircleIcon" size="48" class="text-gray-400 mx-auto mb-2" />
                        <p class="text-gray-500">No statistics available</p>
                    </div>
                </div>
            </vx-card>

            <!-- My Leaves Card -->
            <vx-card :title="`My Leave Requests [${current_year}]`" class="mb-3">
                <template slot="actions">
                    <div class="flex">
                        <vs-button v-if="my_leaves && my_leaves.length > 0" color="primary" type="filled" size="small"
                            @click="onClickCreateLeave()" icon-pack="feather" icon="icon-plus" class="ml-2">
                            Apply for Leave
                        </vs-button>
                        <vs-button color="primary" type="border" size="small" @click="fetchMyLeaves()"
                            icon-pack="feather" icon="icon-refresh-cw" class="ml-2">
                            Refresh
                        </vs-button>
                    </div>
                </template>

                <div v-if="my_leaves && my_leaves.length > 0">
                    <!-- new view -->
                    <vs-table stripe class="mt-8" :data="my_leaves" :sst="true"
                        :max-items="(meta.per_page) ? meta.per_page : 20">
                        <template slot="thead">
                            <vs-th>Leave Period</vs-th>
                            <vs-th>Type & Duration</vs-th>
                            <vs-th>Request Approval From</vs-th>
                            <vs-th>Status</vs-th>
                            <vs-th>Status Changed By</vs-th>
                            <vs-th>Applied Date</vs-th>
                            <vs-th width="20%">Reason</vs-th>
                            <vs-th width="10%"></vs-th>
                        </template>

                        <template slot-scope="{data}">
                            <vs-tr class="cursor-pointer" :key="indextr" v-for="(leave, indextr) in data">

                                <vs-td>
                                    {{ formatDateRange(leave.start_date, leave.end_date) }}
                                </vs-td>
                                <vs-td>
                                    <div class="text-sm text-gray-900">{{ leave.leave_type }}</div>
                                    <div class="text-sm text-gray-500">
                                        {{ leave.total_days }} {{ leave.total_days == 1 ? 'day' : 'days' }}
                                    </div>
                                </vs-td>
                                <vs-td>
                                        <span v-if="leave.leave_reviewer_id">
                                            {{ leave.leave_reviewer.name }}
                                        </span>
                                        <span v-else>N/A</span>
                                </vs-td>

                                <vs-td>
                                    <vs-chip :color="getStatusClass(leave.status)" transparent>
                                        {{ leave.status }}
                                    </vs-chip>
                                </vs-td>
                                <vs-td>
                                    {{ leave.approver ? leave.approver.name : 'N/A' }}
                                </vs-td>
                                <vs-td>
                                    {{ formatDateTime(leave.created_at) }}
                                </vs-td>

                                <vs-td>
                                    <div class="text-sm text-gray-900 max-w-xs">
                                        <vx-read-more :text="leave.reason"/>
                                    </div>
                                    <div v-if="leave.notes" class="text-sm text-gray-500 max-w-xs" style="white-space: pre-line;">
                                        <vx-read-more :text="leave.notes || ''" />
                                    </div>
                                </vs-td>
                                <vs-td>
                                    <div class="flex items-center" style="gap: 4px;">
                                        <vx-tooltip text="View Logs" class="mr-2">
                                            <vs-button size="small" icon-pack="feather" type="border" color="primary"
                                                icon="icon-list"
                                                @click.stop="$refs.leaveRequestLogsPopUp.openPopup(leave.id)"></vs-button>
                                        </vx-tooltip>
                                        <vx-tooltip text="Modify Leave Request" v-if="leave.status === 'Pending'">
                                            <vs-button size="small" icon-pack="feather" type="border" class="mr-2"
                                                icon="icon-edit"
                                                @click.stop="onClickModifyLeaveRequest(leave.id)"></vs-button>
                                        </vx-tooltip>
                                        <vx-tooltip text="Cancel Leave Request" v-if="leave.status === 'Pending'">
                                            <vs-button size="small" icon-pack="feather" type="border" color="danger"
                                                icon="icon-x"
                                                @click.stop="onClickCancelRequestConfirm(leave.id)"></vs-button>
                                        </vx-tooltip>
                                        <vx-tooltip text="Cancel Leave Request" v-if="leave.status === 'Approved' && leave.cancel_request === null">
                                            <vs-button size="small" color="danger" type="border"
                                                    icon-pack="feather" icon="icon-x" @click.stop="onClickCancelRequestConfirm(leave.id, `Approved`)">Cancel</vs-button>
                                        </vx-tooltip>
                                        <span v-if="leave.cancel_request && leave.cancel_request.status === 'Pending'" class="text-xs text-danger ml-1">
                                            Note: Cancellation Request {{ leave.cancel_request.status }}
                                        </span>
                                    </div>
                                </vs-td>
                            </vs-tr>
                        </template />
                    </vs-table>
                    <vs-pagination v-on:changeMaxItems="changePage" :size-array="meta.total ? meta.total : 20"
                        :max-items="perPage" description-body="Rows per page" description-connector="of"
                        description-title="Records" description :description-items="descriptionItems"
                        :total="(meta.last_page) ? meta.last_page : 0" v-model="page" class="p-5">
                    </vs-pagination>
                </div>

                <!-- Empty State -->
                <div v-else class="flex flex-col items-center justify-center py-12">
                    <feather-icon icon="CalendarIcon" size="64" class="text-gray-300 mb-4" />
                    <h3 class="text-lg font-medium text-gray-900 mb-2">No Leave Requests</h3>
                    <p class="text-gray-500 text-center max-w-sm">
                        You haven't submitted any leave requests yet. Click the button below to apply for leave.
                    </p>
                    <vs-button color="primary" type="filled" class="mt-4" icon-pack="feather" icon="icon-plus"
                        @click.stop="onClickCreateLeave()">
                        Apply for Leave
                    </vs-button>
                </div>
            </vx-card>
        </div>
        <vs-prompt 
        @cancel="cancel_leave_obj = {}" 
        @accept="handleCancelLeaveRequest" 
        @close="activeCancelPrompt = false"
        :title="cancel_leave_obj.type === 'Approved' ? 'Request to cancel approved leave' : 'Cancel leave request'"
        :accept-text="'Confirm'" 
        :color="cancel_leave_obj.type === 'Approved' ? 'danger' : 'danger'"
        :active.sync="activeCancelPrompt">
        
        <div class="con-exemple-prompt">
            <span>{{ cancel_leave_obj.type === 'Approved' ? 'Reason for cancellation' : 'Are you sure you want to cancel this request? This operation is irreversible.' }}</span>
            
            <vs-textarea 
            v-if="cancel_leave_obj.type === 'Approved'"
            class="mt-3 w-full" 
            v-model="cancel_leave_obj.reason"
            placeholder="Enter reason for cancellation">
            </vs-textarea>
        </div>
        </vs-prompt>
        <CreateLeavesPopUp ref="createLeavesPopUp" />
        <status-log-pop-up
            ref="leaveRequestLogsPopUp"
            title="Leave Request Logs"
            :columns="leaveLogColumns"
            :fetch-fn="fetchLeaveLogs">
            <template #rows="{ data, buildDiffRows }">
                <vs-tr :key="i" v-for="(_, i) in data">
                    <vs-td :data="data[i].created_at">{{ __dateTimeFormatter(data[i].created_at) }}</vs-td>
                    <vs-td :data="data[i].event">{{ data[i].event }}</vs-td>
                    <vs-td :data="data[i].causer">{{ data[i].causer ? data[i].causer.name : '' }}</vs-td>
                    <template slot="expand">
                        <div class="con-expand-users w-4/5 p-4">
                            <p v-if="data[i].properties && data[i].properties.data && data[i].properties.data.reason" class="text-sm mb-4"><strong>Reason:</strong> {{ data[i].properties.data.reason }}</p>
                            <vs-table v-if="buildDiffRows(data[i].properties).length" noDataText="" class="w-full">
                                <template slot="thead">
                                    <vs-th>FIELD</vs-th>
                                    <vs-th>PREVIOUS VALUE</vs-th>
                                    <vs-th>NEW VALUE</vs-th>
                                </template>
                                <template>
                                    <vs-tr v-for="(row, j) in buildDiffRows(data[i].properties)" :key="j">
                                        <vs-td>{{ row.field }}</vs-td>
                                        <vs-td>{{ row.old }}</vs-td>
                                        <vs-td>{{ row.new }}</vs-td>
                                    </vs-tr>
                                </template>
                            </vs-table>
                            <p v-else class="text-grey text-sm">No field changes recorded for this event.</p>
                        </div>
                    </template>
                </vs-tr>
            </template>
        </status-log-pop-up>
    </div>
</template>

            <script>
            import { RepositoryFactory } from '@/repository/RepositoryFactory';
            const LeaveRepository = RepositoryFactory.get('leave');
            import CreateLeavesPopUp from "@/components/popups/manage-leaves/CreateLeavesPopUp.vue"
            import StatusLogPopUp from "@/components/popups/activity-logs/StatusLogPopUp.vue"
            import { defaultPagination, perPageList } from "@/constants/config";
            import vSelect from 'vue-select'

            export default {
                name: "ManageMyLeaves",
                data() {
                    return {
                        my_leaves: [],
                        my_leaves_stats: {},
                        current_year: new Date().getFullYear(),
                        page: 1,
                        total: 0,
                        meta: {},
                        sort: '',
                        perPage: defaultPagination,
                        descriptionItems: perPageList,
                        year_list: [],
                        activeCancelPrompt: false,
                        cancel_leave_obj: {},
                        leaveLogColumns: [
                            { label: 'DATE/TIME',  width: '30%' },
                            { label: 'EVENT TYPE', width: '30%' },
                            { label: 'DONE BY',    width: '40%' },
                        ],
                    }
                },
                components: {
                    CreateLeavesPopUp,
                    StatusLogPopUp,
                    'v-select': vSelect,
                },
                async mounted() {
                    await this.fetchMyLeavesStats()
                    await this.fetchMyLeaves()
                },
                created() {
                    const year = new Date().getFullYear();
                    this.year_list = [String(year - 1), String(year), String(year + 1)];
                },
                methods: {
                    async fetchLeaveLogs(id, page, perPage) {
                        const response = (await LeaveRepository.getLeaveRequestLogs(id, page, '', perPage, 0)).data
                        return { logs: response.data || [], meta: response.meta }
                    },
                    isStartDateInPast(leave) {
                        if (!leave.start_date) return false;
                        
                        // Parse DD/MM/YYYY format
                        const parts = leave.start_date.split('/');
                        const startDate = new Date(parts[2], parts[1] - 1, parts[0]); // year, month (0-indexed), day
                        
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        
                        return startDate < today;
                    },
                    async refreshData() {
                        await this.fetchMyLeavesStats()
                        await this.fetchMyLeaves()
                    },
                    async onChangeYear() {
                        this.page = 1
                        await this.refreshData()
                    },
                    async fetchMyLeaves() {
                        await this.$vs.loading({
                            container: '#leaves-manage-table',
                            scale: 0.45
                        })
                        try {
                            const { data } = (await LeaveRepository.getMyLeaves(this.current_year, this.page, this.perPage))
                            this.my_leaves = data.data
                            this.meta = data.meta
                        } catch (error) {
                            console.error('Error fetching leaves:', error)
                            this.$vs.notify({
                                title: 'Error',
                                text: 'Failed to fetch leave requests',
                                color: 'danger'
                            })
                        } finally {
                            this.$vs.loading.close('#leaves-manage-table > .con-vs-loading')
                        }
                    },
                    async onClickCreateLeave() {
                        this.$refs.createLeavesPopUp.openPopup()
                    },
                    async onClickModifyLeaveRequest(id) {
                        this.$refs.createLeavesPopUp.openPopup(id)
                    },
                    onClickCancelRequestConfirm(id, type) {
                    this.cancel_leave_obj = {
                        id: id,
                        type: type,
                        reason: ''
                    }
                    this.activeCancelPrompt = true
                    },
                    onClickCancelButton(id, type) {
                        this.cancel_leave_obj = {
                        id: id,
                        type: type,
                        reason: ''
                        }
                        this.activeCancelPrompt = true
                    },

                    handleCancelLeaveRequest() {
                        if (this.cancel_leave_obj.type === 'Approved') {
                        this.onClickRequestToCancelLeaveRequest(
                            this.cancel_leave_obj.id, 
                            this.cancel_leave_obj.reason
                        )
                        } else {
                        this.onClickCancelLeaveRequest(this.cancel_leave_obj.id)
                        }
                        
                        this.cancel_leave_obj = {}
                    },
                    async onClickCancelLeaveRequest(id) {
                    try {
                        await LeaveRepository.changeLeaveRequestStatus(id, 'cancel', {})
                        await this.refreshData()
                    } catch (error) {
                        // handle error
                    }
                    },

                    async onClickRequestToCancelLeaveRequest(id, reason) {
                    try {
                        await LeaveRepository.changeLeaveRequestCancellation(id, 'cancellations', { reason: reason })
                        await this.refreshData()
                    } catch (error) {
                        // handle error
                    }
                    },
                    async fetchMyLeavesStats() {
                        try {
                            const { data } = (await LeaveRepository.getMyLeaveStats(this.current_year)).data
                            this.my_leaves_stats = data
                        } catch (error) {
                            console.error('Error fetching leave stats:', error)
                            this.$vs.notify({
                                title: 'Error',
                                text: 'Failed to fetch leave statistics',
                                color: 'danger'
                            })
                        }
                    },
                    getStatusClass(status) {
                        const classes = {
                            'Approved': 'primary',
                            'Rejected': 'danger',
                            'Pending': 'warning'
                        }
                        return classes[status] || 'dark'
                    },
                    formatDateRange(startDate, endDate) {
                        if (startDate === endDate) {
                            return this.formatDate(startDate)
                        }
                        return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`
                    },
                    formatDate(dateString) {
                        // Convert from DD/MM/YYYY to readable format
                        const [day, month, year] = dateString.split('/')
                        const date = new Date(year, month - 1, day)
                        return date.toLocaleDateString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                        })
                    },
                    formatDateTime(dateTimeString) {
                        const date = new Date(dateTimeString)
                        return date.toLocaleDateString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })
                    },
                    getUsagePercentage() {
                        if (!this.my_leaves_stats || !this.my_leaves_stats.stats || !this.my_leaves_stats.stats.entitlement) return 0
                        const entitlement = this.my_leaves_stats.stats.entitlement
                        const totalUsed = parseFloat(entitlement.used) + parseFloat(entitlement.pending)
                        return Math.round((totalUsed / entitlement.total_allowance) * 100)
                    },
                    changePage(index) {
                        this.perPage = this.descriptionItems[index]
                    },
                },
                watch: {
                    page() {
                        this.fetchMyLeaves()
                    },
                    perPage() {
                        this.fetchMyLeaves()
                    }
                },
            }
</script>

            <style lang="scss" scoped>
            .stat-card {
                transition: all 0.3s ease;
                &:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
                }
            }

            // Custom gradient backgrounds for stat cards
            .stat-card-blue {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white !important;
            }

            .stat-card-orange {
                background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
                color: white !important;
            }

            .stat-card-yellow {
                background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
                color: #333 !important;
            }

            .stat-card-green {
                background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
                color: #333 !important;
            }

            // Progress bar
            .progress-bar {
                background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
            }

            // Grid layout
            .grid {
                display: grid;
                gap: 1rem;
            }

            .grid-cols-1 {
                grid-template-columns: repeat(1, minmax(0, 1fr));
            }

            @media (min-width: 768px) {
                .md\\:grid-cols-4 {
                    grid-template-columns: repeat(4, minmax(0, 1fr));
                }
            }

            // Responsive adjustments
            @media (max-width: 768px) {
                .stat-card {
                    margin-bottom: 1rem;
                }

                .block {
                    display: block;
                }

                .md\\:hidden {
                    display: none;
                }

                .hidden {
                    display: none;
                }
            }

            @media (min-width: 768px) {
                .md\\:block {
                    display: block;
                }

                .md\\:hidden {
                    display: none;
                }
            }

            // Table styles
            .overflow-x-auto {
                overflow-x: auto;
            }

            tbody tr:hover {
                background-color: #f8f9fa;
            }

            // Utility classes
            .flex {
                display: flex;
            }

            .items-center {
                align-items: center;
            }

            .justify-between {
                justify-content: space-between;
            }

            .text-white {
                color: white !important;
            }

            .text-sm {
                font-size: 0.875rem;
            }

            .text-xs {
                font-size: 0.75rem;
            }

            .text-2xl {
                font-size: 1.5rem;
            }

            .font-medium {
                font-weight: 500;
            }

            .font-bold {
                font-weight: 700;
            }

            .mb-1 {
                margin-bottom: 0.25rem;
            }

            .mb-2 {
                margin-bottom: 0.5rem;
            }

            .mr-2 {
                margin-right: 0.5rem;
            }

            .p-4 {
                padding: 1rem;
            }

            .rounded {
                border-radius: 0.375rem;
            }
        </style>